import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";
import { ChevronRight, ChevronLeft, MapPin, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const DIETARY_OPTIONS = [
  { id: "vegetarian", label: "Vegetarian" },
  { id: "vegan", label: "Vegan" },
  { id: "gluten_free", label: "Gluten-Free" },
  { id: "halal", label: "Halal" },
  { id: "kosher", label: "Kosher" },
  { id: "dairy_free", label: "Dairy-Free" },
  { id: "nut_free", label: "Nut-Free" },
  { id: "none", label: "No Restrictions" },
];

const FOOD_CATEGORIES = [
  "Coffee", "Ice Cream", "Pizza", "Mexican", "Asian", "Burgers",
  "Desserts", "Fine Dining", "Fast Food", "Breakfast", "BBQ",
  "Seafood", "Italian", "Sushi", "Bakery", "Smoothies",
];

const CHANNELS = [
  { id: "friends", label: "Friends or Family" },
  { id: "social_media", label: "Social Media" },
  { id: "google", label: "Google Search" },
  { id: "found_app", label: "Just Found This App" },
  { id: "influencer", label: "Influencer / Content Creator" },
  { id: "event", label: "At an Event" },
];

const STEPS = [
  { id: "birthday", title: "When's your birthday?", subtitle: "We'll make sure you never miss a birthday deal" },
  { id: "wrapped_year", title: "Birthday Wrapped", subtitle: "How should we wrap your year?" },
  { id: "city", title: "Where do you celebrate?", subtitle: "We'll show you the best deals nearby" },
  { id: "dietary", title: "Any dietary preferences?", subtitle: "We'll filter out places that don't fit" },
  { id: "categories", title: "What sounds good?", subtitle: "Pick as many as you like" },
  { id: "channel", title: "How did you find us?", subtitle: "Select all that apply" },
];

export default function UserOnboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  // iter314 — skip the duplicate birthday step when the user already
  // locked their birthday during signup (every account created via
  // the iter314 single-screen signup carries a locked birthday).
  // Before iter314 birthday was collected at signup AND re-asked here,
  // which is the duplication the user flagged.
  const birthdayAlreadyLocked = Boolean(
    user?.birthday_locked ||
    (user?.birthday_month && user?.birthday_day)
  );
  const [step, setStep] = useState(birthdayAlreadyLocked ? 1 : 0);
  const [saving, setSaving] = useState(false);
  const [birthdayConfirmed, setBirthdayConfirmed] = useState(birthdayAlreadyLocked);

  // Form state
  const [birthday, setBirthday] = useState({ month: user?.birthday_month || "", day: user?.birthday_day || "" });
  const [city, setCity] = useState(user?.city || "");
  const [detectingCity, setDetectingCity] = useState(false);
  const [dietary, setDietary] = useState({});
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [wrappedYearMode, setWrappedYearMode] = useState("calendar");

  const currentStep = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const detectLocation = () => {
    if (!navigator.geolocation) return;
    setDetectingCity(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await axios.get(`${API}/geocode/reverse?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
          if (res.data?.city) setCity(res.data.city);
        } catch {
          /* geocoding fallback: ignore — city stays whatever the user typed */
        }
        setDetectingCity(false);
      },
      () => setDetectingCity(false),
      { timeout: 5000 }
    );
  };

  const toggleDietary = (id) => {
    if (id === "none") {
      setDietary({ none: !dietary.none });
    } else {
      setDietary(prev => ({ ...prev, [id]: !prev[id], none: false }));
    }
  };

  const toggleCategory = (cat) => {
    setCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const toggleChannel = (id) => {
    setChannels(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const handleNext = () => {
    if (step === 0 && !birthday.month) {
      toast.error("Please select your birthday month");
      return;
    }
    if (step === 0 && birthday.month && !birthdayConfirmed) {
      toast.error("Please confirm your birthday is correct");
      return;
    }
    // Lock birthday when advancing past step 0
    if (step === 0 && birthday.month) {
      axios.put(`${API}/auth/lock-birthday`).catch(() => {});
    }
    if (isLast) {
      handleComplete();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/auth/onboarding-preferences`, {
        dietary_preferences: dietary,
        food_preferences: categories,
        acquisition_channel: channels,
        city: city || undefined,
        wrapped_year_mode: wrappedYearMode,
      });
      if (refreshUser) await refreshUser();
      navigate("/");
    } catch (e) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const daysInMonth = birthday.month ? new Date(2024, parseInt(birthday.month), 0).getDate() : 31;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Progress bar */}
        <div className="flex gap-1.5 mb-8">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 rounded-full flex-1 transition-colors ${i <= step ? "bg-sky-500" : "bg-slate-200"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.2 }}
          >
            <h2 className="text-xl font-bold text-slate-800" data-testid={`onboard-title-${currentStep.id}`}>{currentStep.title}</h2>
            <p className="text-sm text-slate-500 mt-1 mb-6">{currentStep.subtitle}</p>

            {/* Step 1: Birthday */}
            {step === 0 && (
              <div className="space-y-4" data-testid="onboard-birthday">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Month</label>
                  <select
                    value={birthday.month}
                    onChange={e => setBirthday(p => ({ ...p, month: e.target.value }))}
                    className="w-full p-3 rounded-xl border border-slate-200 bg-white text-slate-800"
                    data-testid="birthday-month-select"
                  >
                    <option value="">Select month</option>
                    {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Day</label>
                  <select
                    value={birthday.day}
                    onChange={e => setBirthday(p => ({ ...p, day: e.target.value }))}
                    className="w-full p-3 rounded-xl border border-slate-200 bg-white text-slate-800"
                    data-testid="birthday-day-select"
                  >
                    <option value="">Select day</option>
                    {Array.from({ length: daysInMonth }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                  </select>
                </div>

                {/* Birthday lock warning */}
                {birthday.month && (
                  <div data-testid="birthday-lock-warning">
                    <div className="w-full px-3 py-3 rounded-xl bg-amber-50 border border-amber-200">
                      <p className="text-sm font-bold text-amber-800 flex items-center gap-1.5">
                        <span>&#9888;&#65039;</span> This is your only chance to enter your birthday correctly. You cannot change it after this step.
                      </p>
                    </div>
                    <label className="flex items-center gap-2 mt-3 cursor-pointer" data-testid="birthday-confirm-label">
                      <input
                        type="checkbox"
                        checked={birthdayConfirmed}
                        onChange={(e) => setBirthdayConfirmed(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-400"
                        data-testid="birthday-confirm-checkbox"
                      />
                      <span className="text-sm text-slate-700 font-medium">I confirm this is my correct birthday</span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Wrapped Year Preference */}
            {step === 1 && (() => {
              const bMonth = parseInt(birthday.month) || 1;
              const bDay = parseInt(birthday.day) || 1;
              const bdayLabel = `${months[bMonth - 1]} ${bDay}`;
              const endMonth = bMonth === 1 ? 12 : bMonth - 1;
              const endDay = new Date(2024, endMonth, 0).getDate();
              const endDayVal = Math.min(bDay > 1 ? bDay - 1 : endDay, endDay);
              const endLabel = `${months[endMonth - 1]} ${endDayVal}`;

              return (
                <div className="space-y-3" data-testid="onboard-wrapped-year">
                  <button
                    onClick={() => setWrappedYearMode("calendar")}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      wrappedYearMode === "calendar"
                        ? "bg-sky-500 border-sky-500 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:border-sky-300"
                    }`}
                    data-testid="wrapped-calendar-btn"
                  >
                    <p className="text-sm font-bold">Calendar Year</p>
                    <p className={`text-xs mt-0.5 ${wrappedYearMode === "calendar" ? "text-sky-100" : "text-slate-400"}`}>
                      January 1 — December 31
                    </p>
                  </button>
                  <button
                    onClick={() => setWrappedYearMode("birthday")}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      wrappedYearMode === "birthday"
                        ? "bg-sky-500 border-sky-500 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:border-sky-300"
                    }`}
                    data-testid="wrapped-birthday-btn"
                  >
                    <p className="text-sm font-bold">Your Birthday</p>
                    <p className={`text-xs mt-0.5 ${wrappedYearMode === "birthday" ? "text-sky-100" : "text-slate-400"}`}>
                      {bdayLabel} — {endLabel}
                    </p>
                  </button>
                </div>
              );
            })()}

            {/* Step 3: City */}
            {step === 2 && (
              <div className="space-y-3" data-testid="onboard-city">
                <button
                  onClick={detectLocation}
                  disabled={detectingCity}
                  className="w-full p-3 rounded-xl border border-sky-200 bg-sky-50 text-sky-700 text-sm font-medium flex items-center justify-center gap-2 hover:bg-sky-100 transition"
                  data-testid="detect-location-btn"
                >
                  {detectingCity ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                  {detectingCity ? "Detecting..." : "Use my current location"}
                </button>
                <div className="text-center text-xs text-slate-400">or enter manually</div>
                <input
                  type="text"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  placeholder="Sacramento, CA"
                  className="w-full p-3 rounded-xl border border-slate-200 bg-white text-slate-800"
                  data-testid="city-input"
                />
              </div>
            )}

            {/* Step 4: Dietary Preferences */}
            {step === 3 && (
              <div className="grid grid-cols-2 gap-2" data-testid="onboard-dietary">
                {DIETARY_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => toggleDietary(opt.id)}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      dietary[opt.id]
                        ? "bg-sky-500 border-sky-500 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:border-sky-300"
                    }`}
                    data-testid={`dietary-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* Step 5: Food Categories (no limit) */}
            {step === 4 && (
              <div className="flex flex-wrap gap-2" data-testid="onboard-categories">
                {FOOD_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                      categories.includes(cat)
                        ? "bg-sky-500 border-sky-500 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:border-sky-300"
                    }`}
                    data-testid={`cat-${cat.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Step 6: Acquisition Channel (multi-select) */}
            {step === 5 && (
              <div className="space-y-2" data-testid="onboard-channels">
                {CHANNELS.map(ch => (
                  <button
                    key={ch.id}
                    onClick={() => toggleChannel(ch.id)}
                    className={`w-full p-3 rounded-xl border text-sm font-medium text-left transition-all ${
                      channels.includes(ch.id)
                        ? "bg-sky-500 border-sky-500 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:border-sky-300"
                    }`}
                    data-testid={`channel-${ch.id}`}
                  >
                    {ch.label}
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={() => step > 0 ? setStep(s => s - 1) : navigate(-1)}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            data-testid="onboard-back-btn"
          >
            <ChevronLeft className="w-4 h-4" />Back
          </button>

          <div className="flex gap-2">
            {step > 0 && !isLast && (
              <button
                onClick={() => setStep(s => s + 1)}
                className="px-4 py-2 rounded-xl text-sm text-slate-400 hover:text-slate-600"
                data-testid="onboard-skip-btn"
              >
                Skip
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={saving || (step === 0 && birthday.month && !birthdayConfirmed)}
              className="px-6 py-2 rounded-xl bg-sky-500 text-white text-sm font-bold hover:bg-sky-600 transition flex items-center gap-1 disabled:opacity-50"
              data-testid="onboard-next-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isLast ? "Get Started" : "Next"}
              {!isLast && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
